import React, { useState, useEffect, useRef } from 'react';
import { CaesuraChapterData, ArtbookPlate, CaesuraSegment } from '../types/caesuraEngine';
import { ACT_01_CH_01_DATA } from '../data/chapters/act01_ch01';

interface CaesuraReaderProps {
  chapterData?: CaesuraChapterData;
  onClose?: () => void;
}

export const CaesuraReader: React.FC<CaesuraReaderProps> = ({
  chapterData = ACT_01_CH_01_DATA,
  onClose,
}) => {
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
  const [speakingWordIndex, setSpeakingWordIndex] = useState<number | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isHeartbeatPulsing, setIsHeartbeatPulsing] = useState(false);
  const [isCaesuraGlitching, setIsCaesuraGlitching] = useState(false);
  const [cycleCount, setCycleCount] = useState(51);
  const [artbookDrawerOpen, setArtbookDrawerOpen] = useState(false);
  const [selectedPlate, setSelectedPlate] = useState<ArtbookPlate | null>(null);

  const heartbeatRef = useRef<number | null>(null);

  // 0.75 Hz Mechanical Heartbeat simulation (1.33 seconds per tick)
  useEffect(() => {
    heartbeatRef.current = window.setInterval(() => {
      setIsHeartbeatPulsing(true);
      setTimeout(() => setIsHeartbeatPulsing(false), 120);

      setCycleCount(prev => {
        const next = prev + 1;
        if (next % 53 === 0) {
          setIsCaesuraGlitching(true);
          setTimeout(() => setIsCaesuraGlitching(false), 240);
        }
        return next;
      });
    }, 1333);

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, []);

  const currentSegment = chapterData.segments[activeSegmentIndex] || chapterData.segments[0];

  // Helper to colorize and format speaker tags
  const getSpeakerBadge = (speaker: string) => {
    switch (speaker) {
      case 'ELDER_NARRATOR':
        return { label: 'ELDER CHRONICLER', color: '#ffb700', bg: 'rgba(255,183,0,0.1)' };
      case 'MARCUS_WARD':
        return { label: 'MARCUS WARD // CHIEF ENGINEER', color: '#ff5500', bg: 'rgba(255,85,0,0.15)' };
      case 'ELENA_CRUZ':
        return { label: 'ELENA CRUZ // CHIEF ARCHITECT', color: '#00f3ff', bg: 'rgba(0,243,255,0.12)' };
      case 'SILAS_VANCE':
        return { label: 'SILAS VANCE // COMMS CADET', color: '#38bdf8', bg: 'rgba(56,189,248,0.15)' };
      case 'INQUISITOR_THORNE':
        return { label: 'INQUISITOR THORNE // TRIBUNAL', color: '#ef4444', bg: 'rgba(239,68,68,0.2)' };
      case 'MARGIN_SPECTATOR':
        return { label: 'MARGIN SPECTATOR // THE PAUSE', color: '#c084fc', bg: 'rgba(192,132,252,0.2)' };
      case 'MAMA_JO_VANE':
        return { label: 'MAMA JO VANE // KILO-6 MESS BOSS', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' };
      case 'BENNY_DROST':
        return { label: 'TICK-TOCK BENNY // HAWSER WALKER', color: '#10b981', bg: 'rgba(16,185,129,0.15)' };
      case 'DR_ARTHUR_CHEN':
        return { label: 'DR. CHEN // SAWBONES', color: '#a3e635', bg: 'rgba(163,230,53,0.15)' };
      default:
        return { label: speaker, color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' };
    }
  };

  const currentBadge = getSpeakerBadge(currentSegment.speaker);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#04070b',
        color: '#d8e2ec',
        fontFamily: "'Georgia', serif",
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: isHeartbeatPulsing ? '2px solid rgba(255, 85, 0, 0.6)' : '2px solid #162030',
        boxShadow: isCaesuraGlitching ? 'inset 0 0 80px rgba(0, 243, 255, 0.4), inset 0 0 120px rgba(239, 68, 68, 0.4)' : 'none',
        transition: 'border 0.08s ease-out',
      }}
    >
      {/* Top Telemetry & Acoustic Banner */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 24px',
          background: '#070c14',
          borderBottom: '1px solid #1c2b42',
          fontFamily: "'Segoe UI', monospace",
          fontSize: '0.78rem',
          letterSpacing: '1px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ color: '#ff5500', fontWeight: 800 }}>USSC GERYON</span>
          <span style={{ color: '#64748b' }}>//</span>
          <span style={{ color: '#00f3ff' }}>4.2 PETAWATTS</span>
          <span style={{ color: '#64748b' }}>//</span>
          <span style={{ color: '#f59e0b' }}>0.75 HZ CARRIER</span>
          <span style={{ color: '#64748b' }}>//</span>
          <span style={{ color: isCaesuraGlitching ? '#ef4444' : '#94a3b8' }}>
            CYCLE: {cycleCount} {cycleCount % 53 === 0 ? '[53RD SHIFT GLITCH]' : ''}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button
            onClick={() => setArtbookDrawerOpen(true)}
            style={{
              background: 'rgba(255, 85, 0, 0.15)',
              border: '1px solid #ff5500',
              color: '#ff8833',
              padding: '6px 14px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: '1.2px',
            }}
          >
            🎨 THE KOVACS LEDGER (ARTBOOK)
          </button>

          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: '#162030',
                border: '1px solid #2d3e58',
                color: '#cbd5e1',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              CLOSE
            </button>
          )}
        </div>
      </div>

      {/* Main Reading & Manuscript Workspace */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Left Segment List & Timeline */}
        <div
          style={{
            width: '320px',
            background: '#060a10',
            borderRight: '1px solid #162438',
            overflowY: 'auto',
            padding: '16px',
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', marginBottom: '12px', letterSpacing: '1px' }}>
            ACT {chapterData.actNumber} // CHAPTER {chapterData.chapterNumber}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: '18px' }}>
            {chapterData.chapterTitle}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {chapterData.segments.map((seg: CaesuraSegment, idx: number) => {
              const badge = getSpeakerBadge(seg.speaker);
              const isActive = idx === activeSegmentIndex;
              return (
                <div
                  key={seg.id}
                  onClick={() => {
                    setActiveSegmentIndex(idx);
                    setSpeakingWordIndex(null);
                  }}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    background: isActive ? 'rgba(0, 243, 255, 0.08)' : '#0a101a',
                    border: isActive ? '1px solid #00f3ff' : '1px solid #152234',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: badge.color }}>
                      {badge.label}
                    </span>
                    <span style={{ fontSize: '0.65rem', color: '#64748b' }}>
                      ZONE: {seg.acousticZoneOverride || chapterData.defaultZone}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color: isActive ? '#f8fafc' : '#94a3b8',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {seg.displayText || seg.text}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Center Reader Deck with Arc-Weld Synchronized Telemetry */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '48px 60px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={{ maxWidth: '820px', width: '100%' }}>
            {/* Active Speaker Dossier Banner */}
            <div
              style={{
                background: currentBadge.bg,
                borderLeft: `4px solid ${currentBadge.color}`,
                padding: '14px 20px',
                borderRadius: '0 8px 8px 0',
                marginBottom: '32px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: '0.85rem', fontWeight: 800, color: currentBadge.color, letterSpacing: '1px' }}>
                  {currentBadge.label}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px', fontFamily: "'Segoe UI', monospace" }}>
                  ACOUSTIC ZONE: {currentSegment.acousticZoneOverride || chapterData.defaultZone} // 24kHz DSP TRANSMITTING
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setIsSpeaking(!isSpeaking)}
                  style={{
                    background: isSpeaking ? '#ef4444' : '#00f3ff',
                    color: isSpeaking ? '#fff' : '#04070b',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    letterSpacing: '1px',
                  }}
                >
                  {isSpeaking ? 'STOP' : 'AUDITION VOICE'}
                </button>
              </div>
            </div>

            {/* Current Spoken Paragraph with Arc-Weld Word Sync */}
            <div
              style={{
                fontSize: '1.35rem',
                lineHeight: 1.85,
                color: '#e2e8f0',
                marginBottom: '40px',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)',
              }}
            >
              {(() => {
                const text = currentSegment.displayText || currentSegment.text || '';
                const words = text.split(/\s+/);
                return words.map((word: string, wIdx: number) => {
                  const isActive = speakingWordIndex === wIdx;
                  const isPast = speakingWordIndex !== null && wIdx < speakingWordIndex;
                  return (
                    <span
                      key={wIdx}
                      onClick={() => setSpeakingWordIndex(wIdx)}
                      style={{
                        display: 'inline-block',
                        marginRight: '0.3em',
                        cursor: 'pointer',
                        padding: '1px 3px',
                        borderRadius: '3px',
                        color: isActive ? '#ff5500' : isPast ? '#94a3b8' : '#e2e8f0',
                        background: isActive ? 'rgba(255, 85, 0, 0.25)' : 'transparent',
                        borderBottom: isActive ? '2px solid #ff5500' : 'none',
                        textShadow: isActive ? '0 0 10px rgba(255, 85, 0, 0.8)' : 'none',
                        transition: 'all 0.1s ease',
                      }}
                    >
                      {word}
                    </span>
                  );
                });
              })()}
            </div>

            {/* Inline Artbook Plate (if unlocked) */}
            {chapterData.artbookPlates?.map((plate: ArtbookPlate) => {
              return (
                <div
                  key={plate.plateId}
                  style={{
                    background: '#090e18',
                    border: '1px solid #1e3048',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    marginBottom: '40px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                  }}
                >
                  <div
                    style={{
                      padding: '12px 18px',
                      background: '#0d1522',
                      borderBottom: '1px solid #1e3048',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#f59e0b', letterSpacing: '1.2px' }}>
                      ARTBOOK PLATE // {plate.title}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                      MARKER: {plate.kiloMarker}
                    </span>
                  </div>

                  {plate.imageSrc && (
                    <div style={{ position: 'relative', height: '380px', overflow: 'hidden' }}>
                      <img
                        src={plate.imageSrc}
                        alt={plate.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          filter: 'contrast(1.15) brightness(0.95)',
                        }}
                      />
                    </div>
                  )}

                  <div style={{ padding: '20px 24px' }}>
                    <div
                      style={{
                        background: 'rgba(255, 85, 0, 0.08)',
                        borderLeft: '3px solid #ff5500',
                        padding: '12px 16px',
                        marginBottom: '14px',
                        fontFamily: "'Courier New', monospace",
                        fontSize: '0.88rem',
                        color: '#ffaa66',
                      }}
                    >
                      <strong style={{ display: 'block', fontSize: '0.72rem', color: '#f59e0b', marginBottom: '4px' }}>
                        MARGIN NOTE // {plate.diegeticMarginNote.author} ({plate.diegeticMarginNote.handwritingStyle})
                      </strong>
                      "{plate.diegeticMarginNote.noteText}"
                    </div>

                    <p style={{ fontSize: '0.92rem', color: '#cbd5e1', lineHeight: 1.7, fontStyle: 'italic' }}>
                      {plate.standaloneLoreEssay}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Standalone Artbook Folio Drawer (The Kovacs Ledger) */}
      {artbookDrawerOpen && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(3, 6, 10, 0.96)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            padding: '36px 48px',
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <div>
              <h1 style={{ fontSize: '1.8rem', color: '#ff5500', fontWeight: 800, letterSpacing: '2px' }}>
                THE KOVACS LEDGER — COLLECTOR'S ARTBOOK FOLIO
              </h1>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '4px' }}>
                Recovered Forensic Blueprints, Autopsy Parchments & Megascale Schematics of the USSC Geryon
              </p>
            </div>

            <button
              onClick={() => setArtbookDrawerOpen(false)}
              style={{
                background: '#ff5500',
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              CLOSE FOLIO
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '28px' }}>
            {chapterData.artbookPlates?.map((plate: ArtbookPlate) => (
              <div
                key={plate.plateId}
                style={{
                  background: '#090e18',
                  border: '1px solid #1c2b42',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedPlate(plate)}
              >
                {plate.imageSrc && (
                  <img src={plate.imageSrc} alt={plate.title} style={{ width: '100%', height: '260px', objectFit: 'cover' }} />
                )}
                <div style={{ padding: '16px 20px' }}>
                  <h3 style={{ color: '#f8fafc', fontSize: '1.1rem', marginBottom: '6px' }}>{plate.title}</h3>
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: 1.5 }}>
                    {plate.standaloneLoreEssay.slice(0, 140)}...
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Full Screen Plate Inspection Modal */}
          {selectedPlate && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.85)',
                zIndex: 10001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
              }}
              onClick={() => setSelectedPlate(null)}
            >
              <div
                style={{
                  maxWidth: '900px',
                  width: '100%',
                  background: '#0d1522',
                  border: '1px solid #ff5500',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 0 50px rgba(255,85,0,0.3)',
                }}
                onClick={e => e.stopPropagation()}
              >
                {selectedPlate.imageSrc && (
                  <img src={selectedPlate.imageSrc} alt={selectedPlate.title} style={{ width: '100%', maxHeight: '500px', objectFit: 'cover' }} />
                )}
                <div style={{ padding: '24px 32px' }}>
                  <h2 style={{ color: '#ff5500', fontSize: '1.4rem', marginBottom: '8px' }}>{selectedPlate.title}</h2>
                  <p style={{ color: '#cbd5e1', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '16px' }}>
                    {selectedPlate.standaloneLoreEssay}
                  </p>
                  <button
                    onClick={() => setSelectedPlate(null)}
                    style={{
                      background: '#162030',
                      border: '1px solid #2d3e58',
                      color: '#cbd5e1',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    CLOSE PREVIEW
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
